import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { AccessDeniedNoticeService } from '../services/common/access-denied-notice.service';
import { DASHBOARD_URL } from '../constants/route-paths';
import { environment } from '../../environments/environment';

/**
 * User-management pages for Admin and SuperAdmin (today: `/mbrs/user-management`).
 *
 * ## A user who cannot manage users is sent to the Dashboard with a toast (P.O., 2026-10-14)
 *
 * "You don't have permission to access this page." is staged on
 * `AccessDeniedNoticeService` (rendered by `MainLayoutComponent`) and the user
 * is redirected to the Dashboard REGARDLESS of their browser history — unlike a
 * refused filing, which returns to the previous page. The redirect replaces the
 * typed URL in history, so Back does not bounce them into the refusal again.
 *
 * This replaced the 2026-10-12 behaviour of rendering the 404 page in place so
 * the page would look nonexistent. The 404 page still serves mistyped URLs via
 * the catch-all route; it is just no longer reached from here.
 *
 * This guard used to return a bare `false`. On a cold load (typed URL,
 * bookmark, refresh) that cancels the only navigation there is, so nothing
 * renders at all — not even the layout — and the user got a white page. Always
 * return a `RedirectCommand`, never a bare `false`.
 *
 * This is presentation, not access control. The route and the component ship
 * in the bundle either way; the real enforcement is the backend's
 * role checks on the user-management endpoints.
 *
 * `canManageUsers` (Admin or SuperAdmin) is fresh here: `isAuthenticatedGuard` re-checks the session on
 * every `/mbrs` page change and runs before this child guard. Both sides read
 * the role from the session cookie's claim, so they cannot disagree; a role
 * change takes effect at the next login on both.
 */
export const isAdminGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const accessDeniedNotice = inject(AccessDeniedNoticeService);

  // Defence in depth — isAuthenticatedGuard at the parent /mbrs route
  // already redirects dev sessions to the dev landing, so this branch
  // typically never fires. Kept so this guard is correct in isolation.
  if (auth.isDev() === true) {
    const slug = environment.devAreaSlug;
    if (slug) router.navigate([`/dev-${slug}`, 'logs', 'api-audit']);
    return false;
  }

  if (auth.canManageUsers()) {
    return true;
  }

  accessDeniedNotice.stageNoPermissionToAccessPage();
  return new RedirectCommand(router.parseUrl(DASHBOARD_URL));
};
